import { makeAutoObservable, runInAction } from 'mobx';
import {
  EXPLAIN_FORMAT,
  FACT_CHECK,
  GENERATE_SIMILAR_QUESTIONS,
  IMPROVE_QUESTION,
} from '../prompts/prompts';
import { callAi, Message } from '../services/aiCall';
import {
  generateFromPastQuizzesPrompt,
  generateQuestionsHelper,
} from '../services/aiService';
import { firebaseService } from '../services/firebaseService';
import { QuestionObj, QuizObj } from '../types/quiz';
import { exportToCanvaCSV, exportToTSV } from '../utils/exportHelpers';
import { quickToast } from '../utils/toast';
import { scratchpadStore } from './Scratchpad';

class QuizStore {
  questions: QuestionObj[] = [];
  quizzes: QuizObj[] = [];
  currentQuizId: string = '';
  categories: string[] = [];
  isInitialized = false;

  constructor() {
    makeAutoObservable(this);
  }

  // === Initialization ===
  async initializeFirebase(userId: string) {
    if (this.isInitialized) return;
    firebaseService.setUserId(userId);

    try {
      const { questions, quizzes, categories } =
        await firebaseService.syncFromFirebase();

      runInAction(() => {
        this.questions = questions;
        this.quizzes = quizzes || [];
        this.categories = categories || [];

        // Set current quiz
        if (this.quizzes.length === 0) {
          this.quizzes.push({
            id: firebaseService.generateId(),
            name: 'Current',
            description: '',
            favourite: false,
            lastUpdated: new Date(),
          });
        }
        // find the most recently updated quiz
        const mostRecentlyUpdatedQuiz =
          this.quizzes.reduce((prev, current) =>
            prev.lastUpdated > current.lastUpdated ? prev : current,
          ) || this.quizzes[0];
        this.currentQuizId = mostRecentlyUpdatedQuiz.id;
        this.isInitialized = true;
      });
    } catch (error) {
      console.error('Failed to initialize from Firebase:', error);
      quickToast('Failed to load data from cloud');
    }
  }

  // === Computed Properties ===
  get scratchpadQuestions() {
    return scratchpadStore.scratchpadQuestions;
  }

  get approvedQuestions() {
    return this.questions
      .filter((q) => q.approved && !q.quizId)
      .slice()
      .reverse();
  }

  get currentQuizName() {
    return this.quizzes.find((q) => q.id === this.currentQuizId)?.name;
  }

  get currentQuizQuestions() {
    return this.questions
      .filter((q) => q.quizId === this.currentQuizId)
      .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  }

  get pastQuizQuestions() {
    return this.questions
      .filter((q) => q.id !== this.currentQuizId)
      .slice()
      .reverse();
  }

  get favouriteQuizzes() {
    return this.quizzes.filter((q) => q.favourite).map((q) => q.name);
  }

  // === Helper Methods ===
  quizQuestions(quizId: string) {
    return this.questions
      .filter((q) => q.quizId === quizId)
      .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
  }

  // === Quiz Management Methods ===
  async createNewQuiz(quizName: string) {
    const newQuiz = {
      id: firebaseService.generateId(),
      name: quizName,
      description: '',
      favourite: false,
      lastUpdated: new Date(),
    };

    runInAction(() => {
      this.quizzes = [newQuiz, ...this.quizzes];
      this.setCurrentQuiz(newQuiz.id);
    });

    await firebaseService.saveQuizzes(this.quizzes);
    return newQuiz;
  }

  async renameQuiz(quizId: string, newName: string) {
    runInAction(() => {
      const quiz = this.quizzes.find((q) => q.id === quizId);
      if (quiz) {
        quiz.name = newName;
        quiz.lastUpdated = new Date();
      }
    });
    await firebaseService.saveQuizzes(this.quizzes);
  }

  async reorderQuiz(quizId: string, newPosition: number) {
    runInAction(() => {
      const quiz = this.quizzes.find((q) => q.id === quizId);
      if (quiz) {
        this.quizzes = this.quizzes.filter((q) => q.id !== quizId);
        this.quizzes.splice(newPosition, 0, quiz);
      }
    });

    await firebaseService.saveQuizzes(this.quizzes);
  }

  async updateQuizDescription(quizId: string, description: string) {
    runInAction(() => {
      const quiz = this.quizzes.find((q) => q.id === quizId);
      if (quiz) {
        quiz.description = description;
        quiz.lastUpdated = new Date();
      }
    });

    await firebaseService.saveQuizzes(this.quizzes);
  }

  async setCurrentQuiz(quizId: string) {
    let quiz = this.quizzes.find((q) => q.id === quizId);
    if (!quiz) {
      await this.createNewQuiz('Current');
    } else {
      this.currentQuizId = quiz.id;
    }
  }

  async deleteQuiz(quizId: string) {
    const quizToDelete = this.quizzes.find((q) => q.id === quizId);
    if (!quizToDelete?.id) {
      throw new Error('Quiz not found or missing firebaseId');
    }

    runInAction(() => {
      this.quizzes = this.quizzes.filter((q) => q.id !== quizId);
      this.questions.forEach((q) => {
        if (q.quizId === quizId) {
          q.quizId = undefined;
        }
      });

      if (this.quizzes.length === 0) {
        const newQuiz = {
          id: firebaseService.generateId(),
          name: 'Current',
          description: '',
          favourite: false,
          lastUpdated: new Date(),
        };
        this.currentQuizId = newQuiz.id;
        this.quizzes.push(newQuiz);
      } else {
        this.currentQuizId = this.quizzes[0].id;
      }
    });

    await Promise.all([
      firebaseService.deleteQuiz(quizToDelete.id),
      firebaseService.saveQuestions(this.questions),
    ]);
  }

  async toggleFavourite(quizId: string) {
    let isFavourite = false;

    runInAction(() => {
      const MAX_FAVOURITE_COUNT = 3;
      let quiz = this.quizzes.find((q) => q.id === quizId);
      if (!quiz) {
        console.error('Quiz not found');
        return;
      }
      if (!quiz.favourite) {
        if (
          this.quizzes.filter((q) => q.favourite).length < MAX_FAVOURITE_COUNT
        ) {
          quiz.favourite = true;
        } else {
          quickToast(`You can only favourite ${MAX_FAVOURITE_COUNT} quizzes`);
        }
      } else {
        quiz.favourite = false;
      }
      isFavourite = quiz.favourite;
    });

    await firebaseService.saveQuizzes(this.quizzes);
    return isFavourite;
  }

  // === AI Generation Methods ===
  async generateFromPrompt(prompt: string) {
    const messages: Message[] = [
      {
        role: 'system',
        content: generateFromPastQuizzesPrompt(
          this.favouriteQuizzes,
          this.questions,
        ),
      },
      { role: 'user', content: prompt },
    ];
    const questions = await generateQuestionsHelper(messages);
    runInAction(() => {
      questions.forEach((q: QuestionObj) =>
        scratchpadStore.addQuestionToScratchpad(q.question, q.answer),
      );
    });
  }

  async generateFromPastQuizzes(quizIds: string[]) {
    let allQuestionsString = '';
    quizIds.forEach((quizId) => {
      const quizName = this.quizzes.find((q) => q.id === quizId)?.name;
      const quizQuestions = this.questions.filter((q) => q.quizId === quizId);
      quizQuestions.sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
      allQuestionsString += `## ${quizName}\n`;
      quizQuestions.forEach((q) => {
        allQuestionsString += `${q.number}. ${q.question} (answer: ${q.answer})\n`;
      });
    });
    const messages: Message[] = [
      {
        role: 'system',
        content: generateFromPastQuizzesPrompt(quizIds, this.questions),
      },
    ];
    const questions = await generateQuestionsHelper(messages);
    runInAction(() => {
      questions.forEach((q: QuestionObj) =>
        scratchpadStore.addQuestionToScratchpad(q.question, q.answer),
      );
    });
  }

  async generateFromFilteredQuestions(inputQuestions: QuestionObj[]) {
    const randomOrder = inputQuestions.sort(() => Math.random() - 0.5);
    const questionsString = randomOrder
      .slice(-8)
      .map((q) => q.question)
      .join('\n');
    const messages: Message[] = [
      { role: 'system', content: EXPLAIN_FORMAT },
      {
        role: 'user',
        content:
          'Generate 10 new trivia questions similar to the following:\n' +
          questionsString,
      },
    ];
    const questions = await generateQuestionsHelper(messages);
    runInAction(() => {
      questions.forEach((q: QuestionObj) =>
        scratchpadStore.addQuestionToScratchpad(q.question, q.answer),
      );
    });
  }

  async improveQuestion(question: string) {
    const messages: Message[] = [
      { role: 'system', content: IMPROVE_QUESTION },
      { role: 'user', content: question },
    ];
    const questions = await generateQuestionsHelper(messages);
    return questions;
  }

  async generateSimilarQuestions(question: string) {
    const messages: Message[] = [
      { role: 'system', content: GENERATE_SIMILAR_QUESTIONS },
      { role: 'user', content: question },
    ];
    const questions = await generateQuestionsHelper(messages);
    runInAction(() => {
      questions.forEach((q: QuestionObj) =>
        scratchpadStore.addQuestionToScratchpad(q.question, q.answer),
      );
    });
  }

  async factCheck(question: QuestionObj) {
    const messages: Message[] = [
      { role: 'system', content: FACT_CHECK },
      {
        role: 'user',
        content: `Q) ${question.question}\nA) ${question.answer}`,
      },
    ];
    const response = await callAi(messages);
    return response;
  }

  // === Question Management Methods ===
  async updateQuestion(
    id: string,
    updatedQuestion: string,
    updatedAnswer: string,
  ) {
    runInAction(() => {
      const question = this.questions.find((q) => q.id === id);
      if (question) {
        question.question = updatedQuestion;
        question.answer = updatedAnswer;
      }
    });

    await firebaseService.saveQuestions(this.questions);
  }

  addQuestionToScratchpad(question: string, answer: string): QuestionObj {
    return scratchpadStore.addQuestionToScratchpad(question, answer);
  }

  async removeQuestions(ids: Set<string>) {
    runInAction(() => {
      this.questions = this.questions.filter(
        (q) => !ids.has(q.id) && (q.quizId || q.approved),
      );
      scratchpadStore.removeQuestions(ids);
    });

    await firebaseService.saveQuestions(this.questions);
  }

  async approveQuestion(id: string) {
    const scratchpadIndex = scratchpadStore.scratchpad.findIndex(
      (q) => q.id === id,
    );

    if (scratchpadIndex !== -1) {
      runInAction(() => {
        const question = scratchpadStore.scratchpad[scratchpadIndex];
        question.approved = true;
        this.questions.push(question);
        scratchpadStore.scratchpad.splice(scratchpadIndex, 1);
      });
      await firebaseService.saveQuestions(this.questions);
      return;
    }

    runInAction(() => {
      const question = this.questions.find((q) => q.id === id);
      if (question) {
        question.approved = true;
      }
    });

    await firebaseService.saveQuestions(this.questions);
  }

  async unapproveQuestion(id: string) {
    const question = this.questions.find((q) => q.id === id);
    if (question) {
      runInAction(() => {
        if (!question.quizId) {
          // If no quizId, move to scratchpad, remove from questions, and delete from Firebase
          scratchpadStore.scratchpad.push({ ...question, approved: false });
          this.questions = this.questions.filter((q) => q.id !== id);
          if (question.id) {
            firebaseService.deleteQuestion(question.id);
          }
        } else {
          // If it has a quizId, just mark as unapproved and update in Firebase
          question.approved = false;
          firebaseService.saveQuestions(this.questions);
        }
      });
    }
  }

  async changeNumber(id: string, newNumber: number) {
    runInAction(() => {
      const question = this.questions.find((q) => q.id === id);
      if (question) {
        // Create a new question object to ensure proper tracking
        const updatedQuestion = { ...question, number: newNumber };
        // Replace the old question with the new one
        const index = this.questions.findIndex((q) => q.id === id);
        this.questions[index] = updatedQuestion;
      }
    });

    await firebaseService.saveQuestions(this.questions);
  }

  async addQuestionToQuiz(id: string) {
    const currentQuizQuestions = this.currentQuizQuestions;
    if (currentQuizQuestions.some((q) => q.id === id)) {
      quickToast('Question already in quiz');
      return;
    }

    const scratchpadIndex = scratchpadStore.scratchpad.findIndex(
      (q) => q.id === id,
    );

    if (scratchpadIndex !== -1) {
      runInAction(() => {
        const question = scratchpadStore.scratchpad[scratchpadIndex];
        const updatedQuestion: QuestionObj = {
          ...question,
          quizId: this.currentQuizId,
          approved: true,
          number:
            Math.max(...currentQuizQuestions.map((q) => q.number ?? 0), 0) + 1,
        };
        this.questions.push(updatedQuestion);
        scratchpadStore.scratchpad.splice(scratchpadIndex, 1);
        this.updateQuizLastModified();
      });

      await firebaseService.saveQuestions(this.questions);
      return;
    }

    const questionIndex = this.questions.findIndex((q) => q.id === id);
    if (questionIndex !== -1) {
      runInAction(() => {
        const updatedQuestion: QuestionObj = {
          ...this.questions[questionIndex],
          quizId: this.currentQuizId,
          approved: true,
          number:
            Math.max(...currentQuizQuestions.map((q) => q.number ?? 0), 0) + 1,
        };
        this.questions[questionIndex] = updatedQuestion;
        this.updateQuizLastModified();
      });

      await firebaseService.saveQuestions(this.questions);
    }
  }

  async removeQuestionFromQuiz(id: string) {
    const question = this.questions.find((q) => q.id === id);
    if (!question) return;

    runInAction(() => {
      if (!question.approved) {
        // If not approved, move to scratchpad and delete from Firestore
        scratchpadStore.scratchpad.push({
          ...question,
          quizId: undefined,
          number: undefined,
        });
        this.questions = [...this.questions.filter((q) => q.id !== id)];
        if (question.id) {
          firebaseService.deleteQuestion(question.id);
        }
      } else {
        // If approved, just update the question properties and create a new array
        const updatedQuestions = this.questions.map((q) => {
          if (q.id === id) {
            return { ...q, quizId: undefined, number: undefined };
          }
          return q;
        });
        this.questions = updatedQuestions;
        firebaseService.saveQuestions([question]);
      }

      // Re-number the quiz using the computed property
      const quizQuestions = this.currentQuizQuestions;
      const updatedQuestions = this.questions.map((q) => {
        if (q.quizId === this.currentQuizId) {
          const index = quizQuestions.findIndex((qq) => qq.id === q.id);
          if (index !== -1) {
            return { ...q, number: index + 1 };
          }
        }
        return q;
      });
      this.updateQuizLastModified();
      this.questions = updatedQuestions;
    });

    await Promise.all([
      firebaseService.saveQuestions(this.questions),
      firebaseService.saveQuizzes(this.quizzes),
    ]);
  }

  private updateQuizLastModified() {
    const currentQuizIndex = this.quizzes.findIndex(
      (q) => q.id === this.currentQuizId,
    );
    if (currentQuizIndex === -1) {
      throw new Error('Current quiz not found');
    }
    this.quizzes[currentQuizIndex] = {
      ...this.quizzes[currentQuizIndex],
      lastUpdated: new Date(),
    };
  }

  // === Category Management Methods ===
  async addCategory(category: string) {
    if (this.categories.includes(category)) {
      alert('Category already exists');
      return;
    }

    runInAction(() => {
      this.categories.push(category);
    });

    await firebaseService.saveCategories(this.categories);
  }

  async removeCategory(category: string) {
    runInAction(() => {
      this.categories = this.categories.filter((c) => c !== category);
    });

    await firebaseService.saveCategories(this.categories);
  }

  async generateQuestionsForCategory(category: string) {
    const messages: Message[] = [
      {
        role: 'system',
        content: generateFromPastQuizzesPrompt(
          this.favouriteQuizzes,
          this.questions,
        ),
      },
      { role: 'user', content: `## Category\n${category}` },
    ];
    const questions = await generateQuestionsHelper(messages);
    runInAction(() => {
      questions.forEach((q: QuestionObj) =>
        scratchpadStore.addQuestionToScratchpad(q.question, q.answer),
      );
    });
  }

  // === Export Methods ===
  exportToTSV(quizId: string) {
    const quiz = this.quizzes.find((q) => q.id === quizId);
    if (!quiz) {
      throw new Error('Quiz not found');
    }
    exportToTSV(
      this.questions.filter((q) => q.quizId === quizId),
      quiz.name,
    );
  }

  exportToCanvaCSV(quizId: string) {
    const quiz = this.quizzes.find((q) => q.id === quizId);
    if (!quiz) {
      throw new Error('Quiz not found');
    }
    exportToCanvaCSV(
      this.questions.filter((q) => q.quizId === quizId),
      quiz.name,
    );
  }

  // === Import Methods ===
  async importFromTSV(file: File | undefined) {
    if (file) {
      const text = await file.text();
      const lines = text.split('\n');
      const questions: QuestionObj[] = [];
      let newQuizzes = new Set<string>();
      let quizNameToId: { [key: string]: string } = {};

      for (let i = 0; i < lines.length; i++) {
        const [quizName, number, question, answer] = lines[i].split('\t');
        // skip empty lines and header
        if (!quizName || quizName === 'quiz name') {
          continue;
        }
        if (
          !newQuizzes.has(quizName) &&
          this.quizzes.every((q) => q.name !== quizName)
        ) {
          newQuizzes.add(quizName);
          quizNameToId[quizName] = firebaseService.generateId();
        }
        questions.push(
          new QuestionObj({
            id: firebaseService.generateId(),
            number: parseInt(number),
            question,
            answer,
            quizId: quizNameToId[quizName],
            approved: true,
          }),
        );
      }

      runInAction(() => {
        this.questions = this.questions.concat(questions);
        this.quizzes = [
          ...Array.from(newQuizzes).map((quiz) => ({
            id: quizNameToId[quiz],
            name: quiz,
            description: '',
            favourite: false,
            lastUpdated: new Date(),
          })),
          ...this.quizzes,
        ];
      });

      await Promise.all([
        firebaseService.saveQuestions(this.questions),
        firebaseService.saveQuizzes(this.quizzes),
      ]);
    }
  }
}

const quizStore = new QuizStore();
export default quizStore;
